/**
 * HomeLiveGames — "LIVE NOW" strip for the home screen.
 * ────────────────────────────────────────────────────────────
 * Shows every college-football game IN PROGRESS right now
 * (GET /api/live-games/college-now), with our onboarded Texas
 * schools pinned to the front. Each compact Liquid-Glass card
 * shows both teams' score + quarter/clock. Tapping a card slides
 * up a modal that renders the full LiveGamePill (the "Liquid Glass
 * full pill") plus the live play-by-play cast.
 *
 * iter697 · Two view modes — a horizontal card strip (default) and
 * a full vertical list, toggled by a Liquid-Glass pill next to the
 * games count. Killed the default button focus ring (the stray blue
 * outline) so the cards stay Liquid-Glass clean.
 *
 * The whole section renders NOTHING when no games are live.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, List, LayoutGrid, Radio } from 'lucide-react';
import LiveGamePill from '../LiveGamePill';
import { FlytLiveMultiGameSheet } from './TrackFlytLive';
import { usePausableInterval } from '../../lib/usePausableInterval';
import { TeamHelmetPhoto, hashColor } from '../TeamHelmet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 25_000;

// iter738 · Was a flat colored Disk — Jay wants the same real-photo
// helmet chip used in the Daily Brief's Scores tab here too. Tailwind
// needs literal class strings to generate them, so the two fixed sizes
// this file actually uses (34px cards, 24px list rows) are spelled out
// rather than built from the `size` number.
const Helmet = ({ team = {}, size = 34 }) => {
  const color = team.color || hashColor(team.abbr || team.name || '?');
  const abbr = team.abbr || team.name || '?';
  const className = size >= 30 ? 'w-[34px] h-[34px] shrink-0' : 'w-6 h-6 shrink-0';
  return <TeamHelmetPhoto color={color} abbr={abbr} className={className} sizePx={size} />;
};

// Shared: strips the default browser focus/tap ring AND draws the glass
// hairline border via an explicit inset box-shadow. We intentionally do
// NOT use Tailwind `ring-*` here — if the ring-color class isn't emitted,
// `ring-1` silently falls back to Tailwind's DEFAULT blue-500 ring, which
// is the stray blue Jay was seeing on iOS. Inline box-shadow can't do that.
const GLASS = {
  WebkitTapHighlightColor: 'transparent',
  outline: 'none',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)',
};
const CLEAN_FOCUS = 'appearance-none outline-none focus:outline-none focus-visible:outline-none';

const LiveGameCard = ({ game, onOpen }) => {
  const { home, away, period_label: period, clock } = game;
  return (
    <button
      type="button"
      onClick={() => onOpen(game)}
      data-testid={`live-now-card-${game.event_id}`}
      className={`group relative shrink-0 w-[236px] text-left rounded-3xl p-3.5 bg-white/[0.05] backdrop-blur-2xl backdrop-saturate-150 hover:bg-white/[0.08] transition-colors overflow-hidden ${CLEAN_FOCUS}`}
      style={GLASS}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-400">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/55 tabular-nums">
          {period}{clock ? ` · ${clock}` : ''}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
          <Helmet team={home} />
          <span className="text-[10px] font-bold text-white/70 truncate max-w-[84px] text-center leading-tight">{home.short_name || home.name}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="font-display text-2xl font-black text-white tabular-nums">{home.score}</span>
          <span className="text-white/30 text-lg font-black">·</span>
          <span className="font-display text-2xl font-black text-white tabular-nums">{away.score}</span>
        </div>
        <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
          <Helmet team={away} />
          <span className="text-[10px] font-bold text-white/70 truncate max-w-[84px] text-center leading-tight">{away.short_name || away.name}</span>
        </div>
      </div>
    </button>
  );
};

const LiveGameRow = ({ game, onOpen }) => {
  const { home, away, period_label: period, clock } = game;
  return (
    <button
      type="button"
      onClick={() => onOpen(game)}
      data-testid={`live-now-row-${game.event_id}`}
      className={`w-full flex items-center gap-3 rounded-2xl px-3.5 py-3 bg-white/[0.05] backdrop-blur-2xl backdrop-saturate-150 hover:bg-white/[0.08] active:scale-[0.99] transition-all ${CLEAN_FOCUS}`}
      style={GLASS}
    >
      {/* status column */}
      <div className="flex flex-col items-start gap-1 w-16 shrink-0">
        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-red-400">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
        </span>
        <span className="text-[9px] font-bold uppercase tracking-widest text-white/50 tabular-nums leading-tight">
          {period}{clock ? ` ${clock}` : ''}
        </span>
      </div>
      {/* teams + scores */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2">
          <Helmet team={home} size={24} />
          <span className="flex-1 text-[12px] font-bold text-white truncate">{home.short_name || home.name}</span>
          <span className="font-display text-lg font-black text-white tabular-nums">{home.score}</span>
        </div>
        <div className="flex items-center gap-2">
          <Helmet team={away} size={24} />
          <span className="flex-1 text-[12px] font-bold text-white/85 truncate">{away.short_name || away.name}</span>
          <span className="font-display text-lg font-black text-white/85 tabular-nums">{away.score}</span>
        </div>
      </div>
    </button>
  );
};

const LiveGameModal = ({ game, onClose }) => {
  const onboardedSlug = game.home.slug || game.away.slug || null;
  const initialGame = onboardedSlug ? null : {
    status: 'live',
    clock: game.clock || '',
    period: game.period_label || '',
    sport: 'cfb',
    event_id: game.event_id,
    is_home: true,
    athlete_team: { name: game.home.name, abbr: game.home.abbr, score: game.home.score, color: game.home.color },
    opponent: { name: game.away.name, abbr: game.away.abbr, score: game.away.score, color: game.away.color },
    athlete_team_id: game.home.team_id,
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="live-now-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-[fadeIn_180ms_ease-out]" />
      <div
        className="relative w-full sm:max-w-md max-h-[90vh] overflow-y-auto bg-neutral-950 rounded-t-3xl sm:rounded-3xl ring-1 ring-white/10 shadow-[0_-18px_60px_rgba(0,0,0,0.7)] p-4 pb-8 animate-[slideUp_260ms_cubic-bezier(0.22,1,0.36,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 sticky top-0 -mt-1 pt-1">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-red-400">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live game
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="live-now-modal-close"
            className={`w-8 h-8 rounded-full bg-white/[0.08] ring-1 ring-white/10 text-white/80 hover:text-white hover:bg-white/[0.14] flex items-center justify-center transition ${CLEAN_FOCUS}`}
            style={{ WebkitTapHighlightColor: 'transparent', outline: 'none' }}
            aria-label="Close live game"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <LiveGamePill school={onboardedSlug || undefined} initialGame={initialGame} />
      </div>
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
};

const HomeLiveGames = () => {
  const [games, setGames] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('strip'); // 'strip' | 'list'
  const [showTracked, setShowTracked] = useState(false);
  const [trackedCount, setTrackedCount] = useState(0);
  const scrollRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const load = useCallback(() => {
    axios.get(`${API}/live-games/college-now`, { timeout: 9000 })
      .then((r) => setGames(Array.isArray(r.data?.games) ? r.data.games : []))
      .catch(() => { /* keep last known list on transient failure */ });
  }, []);

  // iter-perf · Pauses while the app is backgrounded instead of
  // silently burning battery/data polling a screen nobody is looking
  // at, then catches up immediately the moment it's foregrounded again.
  usePausableInterval(load, POLL_MS);

  // How many games this account currently tracks — powers the FLYT Live
  // entry even in the offseason. Only polls while signed in: a 401 stops
  // the interval so anonymous users don't spam the endpoint.
  const [trackedAuthOk, setTrackedAuthOk] = useState(true);
  const fetchTrackedCount = useCallback(() => {
    axios.get(`${API}/live-games/tracked`, { withCredentials: true })
      .then((r) => setTrackedCount((r.data?.games || []).length))
      .catch((e) => { if (e?.response?.status === 401) setTrackedAuthOk(false); });
  }, []);
  usePausableInterval(fetchTrackedCount, 30000, { enabled: trackedAuthOk });

  // Edge-aware fades: only show a fade on a side that still has content to
  // scroll toward, so the strip never looks "cut off" when fully scrolled.
  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setEdges({
      left: scrollLeft > 8,
      right: scrollLeft + clientWidth < scrollWidth - 8,
    });
  }, []);

  useEffect(() => {
    updateEdges();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges, games.length, view]);

  if (!games.length) {
    // Offseason / nothing live right now — but if the account still
    // tracks games, keep a slim FLYT Live entry so they can manage them.
    if (trackedCount > 0) {
      return (
        <section className="px-4 sm:px-6 mt-6" data-testid="home-live-now-idle">
          <button
            type="button"
            onClick={() => setShowTracked(true)}
            data-testid="home-flyt-live-open"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-widest text-black bg-white hover:bg-white/85 active:scale-[0.98] transition-all ${CLEAN_FOCUS}`}
          >
            <Radio className="w-3.5 h-3.5" /> FLYT Live · {trackedCount}
          </button>
          {showTracked && <FlytLiveMultiGameSheet onClose={() => setShowTracked(false)} />}
        </section>
      );
    }
    return null;
  }

  return (
    <section className="px-4 sm:px-6 mt-6" data-testid="home-live-now">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <h2 className="font-display text-lg uppercase tracking-tight text-white">Live Now</h2>
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 bg-white/[0.06] ring-1 ring-white/10 rounded-full px-2 py-0.5 tabular-nums">
          {games.length} {games.length === 1 ? 'game' : 'games'}
        </span>
        {/* FLYT LIVE — open the multi-game tracked sheet */}
        <button
          type="button"
          onClick={() => setShowTracked(true)}
          data-testid="home-flyt-live-open"
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-black bg-white hover:bg-white/85 active:scale-[0.97] transition-all ${CLEAN_FOCUS}`}
        >
          <Radio className="w-3.5 h-3.5" /> FLYT Live
        </button>
        {/* Liquid-Glass view toggle */}
        <button
          type="button"
          onClick={() => setView((v) => (v === 'strip' ? 'list' : 'strip'))}
          data-testid="live-now-view-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/80 bg-white/[0.05] backdrop-blur-2xl backdrop-saturate-150 hover:text-white hover:bg-white/[0.09] active:scale-[0.97] transition-all ${CLEAN_FOCUS}`}
          style={GLASS}
        >
          {view === 'strip' ? (
            <><List className="w-3.5 h-3.5" /> See list view</>
          ) : (
            <><LayoutGrid className="w-3.5 h-3.5" /> See cards</>
          )}
        </button>
      </div>

      {view === 'strip' ? (
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x', overscrollBehaviorX: 'contain', scrollbarWidth: 'none' }}
          >
            {games.map((g) => (
              <LiveGameCard key={g.event_id} game={g} onOpen={setSelected} />
            ))}
          </div>
          {/* edge fades — only where more content exists in that direction */}
          {edges.left && (
            <div className="pointer-events-none absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-black to-transparent" />
          )}
          {edges.right && (
            <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-black to-transparent" />
          )}
        </div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pb-1 scrollbar-hide" data-testid="live-now-list">
          {games.map((g) => (
            <LiveGameRow key={g.event_id} game={g} onOpen={setSelected} />
          ))}
        </div>
      )}

      {selected && <LiveGameModal game={selected} onClose={() => setSelected(null)} />}
      {showTracked && <FlytLiveMultiGameSheet onClose={() => setShowTracked(false)} />}
    </section>
  );
};

export default HomeLiveGames;
